import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const ThemeContext = createContext();

const THEME_KEY = 'forecast_theme';

const lightTheme = {
  mode: 'light',
  backgroundColor: '#ffffff',
  cardBackground: '#F8F8F8',
  surfaceBackground: '#F8F9FA',
  textColor: '#222222',
  secondaryText: '#666666',
  tertiaryText: '#999999',
  disabledText: '#D1D5DB',
  borderColor: '#E5E7EB',
  lightBorder: '#F3F4F6',
  iconButtonBg: '#F3F4F6',
  iconButtonHover: '#E5E7EB',
  pillBg: '#F3F4F6',
  overlayBg: 'rgba(0,0,0,0.3)',
  inputBg: '#ffffff',
  inputBorder: '#E5E7EB',
  hoverBg: '#f9fafb',
  badgeBg: '#F3F4F6',
  activeBg: '#EFF6FF',
  activeAccent: '#2563eb',
  // Tinted backgrounds for summary pills
  blueTint: '#EFF6FF',
  greenTint: '#ECFDF5',
  redTint: '#FFF1F2',
  orangeTint: '#FFF7ED',
  purpleTint: '#F5F3FF',
  // Banner
  bannerBg: '#A3E635',
  bannerText: '#000000',
};

const darkTheme = {
  mode: 'dark',
  backgroundColor: '#000000',
  cardBackground: '#1C1C1E',
  surfaceBackground: '#111111',
  textColor: '#FFFFFF',
  secondaryText: '#EBEBF5',
  tertiaryText: '#ABABAB',
  disabledText: '#3A3A3C',
  borderColor: '#3A3A3C',
  lightBorder: '#2C2C2E',
  iconButtonBg: '#2C2C2E',
  iconButtonHover: '#3A3A3C',
  pillBg: '#1C1C1E',
  overlayBg: 'rgba(0,0,0,0.6)',
  inputBg: '#1C1C1E',
  inputBorder: '#3A3A3C',
  hoverBg: '#1C1C1E',
  badgeBg: '#2C2C2E',
  activeBg: '#1E3A5F',
  activeAccent: '#60A5FA',
  // Tinted backgrounds for summary pills (darker variants)
  blueTint: '#1E3A5F',
  greenTint: '#14332A',
  redTint: '#3D1F23',
  orangeTint: '#3D2E1A',
  purpleTint: '#2D1F4E',
  // Banner
  bannerBg: '#365314',
  bannerText: '#A3E635',
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved) return saved;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const theme = useMemo(() => mode === 'dark' ? darkTheme : lightTheme, [mode]);

  const toggleTheme = () => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      localStorage.setItem(THEME_KEY, next);
      return next;
    });
  };

  // Apply to document — sets CSS variables for use in .css files
  useEffect(() => {
    document.documentElement.style.backgroundColor = theme.backgroundColor;
    document.documentElement.style.colorScheme = mode;
    // Set CSS variables so .css files can use them
    const root = document.documentElement;
    root.style.setProperty('--bg', theme.backgroundColor);
    root.style.setProperty('--surface-bg', theme.surfaceBackground);
    root.style.setProperty('--card-bg', theme.cardBackground);
    root.style.setProperty('--text', theme.textColor);
    root.style.setProperty('--text-secondary', theme.secondaryText);
    root.style.setProperty('--text-tertiary', theme.tertiaryText);
    root.style.setProperty('--border', theme.borderColor);
    root.style.setProperty('--light-border', theme.lightBorder);
    root.style.setProperty('--badge-bg', theme.badgeBg);
    root.style.setProperty('--input-bg', theme.inputBg);
  }, [mode, theme]);

  const value = useMemo(() => ({ theme, mode, toggleTheme }), [theme, mode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
